import { useEffect, useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { LayoutDashboard, Package, Truck, Wallet, LogOut, Store, Menu, X, Crown, Calculator } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const NAV = [
  ["/seller", LayoutDashboard, "Dashboard", true],
  ["/seller/products", Package, "My Products"],
  ["/seller/orders", Truck, "My Orders"],
  ["/seller/pos", Calculator, "Rs Mart POS"],
  ["/seller/wallet", Wallet, "Wallet & Premium"],
];

export default function SellerLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (user === false) { nav("/login?next=/seller"); return; }
    if (user) api.get("/seller/me").then((r) => {
      setMe(r.data);
      if (r.data.status !== "approved") nav("/become-seller");
    }).catch(() => nav("/become-seller"));
  }, [user]);

  if (!me || me.status !== "approved")
    return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="seller-shell-loading">Loading…</div>;

  return (
    <div className="flex h-screen bg-slate-100 overflow-hidden" data-testid="seller-shell">
      <aside className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static z-50 w-64 h-full bg-slate-900 text-slate-400 flex flex-col shrink-0 transition-transform`}>
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center text-white shrink-0"><Store size={16} /></div>
            <div className="min-w-0"><p className="text-white font-bold leading-none truncate" data-testid="seller-firm-name">{me.business_name || "Seller"}</p><p className="text-[10px] text-orange-400">SELLER PANEL</p></div>
          </div>
          <button className="lg:hidden text-slate-400" onClick={() => setOpen(false)}><X size={20} /></button>
        </div>
        {me.premium_active_now && (
          <div className="mx-3 mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/10 text-amber-400 text-[11px] font-bold"><Crown size={13} /> Local Delivery Premium</div>
        )}
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-1">
          {NAV.map(([to, Icon, label, end]) => (
            <NavLink key={to} to={to} end={end} data-testid={`seller-nav-${label.toLowerCase().replace(/[^a-z]/g, "-")}`} onClick={() => setOpen(false)}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-orange-500 text-white" : "hover:bg-slate-800 hover:text-white"}`}>
              <Icon size={18} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="px-3 py-1 text-xs truncate">{user?.email}</div>
          <button data-testid="seller-view-store" onClick={() => nav("/")} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-slate-800 hover:text-white transition-colors"><Store size={16} /> View Store</button>
          <button data-testid="seller-logout" onClick={async () => { await logout(); nav("/"); }} className="mt-1 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-slate-800 hover:text-white transition-colors"><LogOut size={16} /> Logout</button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-slate-200 px-3 lg:px-6 py-3 flex items-center gap-2 shrink-0 lg:hidden">
          <button onClick={() => setOpen(true)} data-testid="seller-menu-btn"><Menu size={22} /></button>
          <span className="font-bold text-slate-800">{me.business_name || "Seller Panel"}</span>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6"><Outlet context={{ me }} /></main>
      </div>
    </div>
  );
}
